/**
 * WebSocket Streaming Client
 *
 * Manages WebSocket connections for real-time mission status updates.
 * Features:
 * - Auto-reconnection with exponential backoff
 * - Heartbeat/keepalive
 * - Mission subscription management
 * - Type-safe message handling
 *
 * Adapted from admin-ui WebSocketStreamingClient for chat-ui.
 * Tool generation subscriptions omitted (admin-only feature).
 */

import { logger } from '@/utils/logger';
import {
  MessageTypeConnectionRestored,
  MessageTypeEscalationAnswerFrame,
  MessageTypeEscalationAnswerResult,
  MessageTypeOperatorEndUserTurnEvent,
  MessageTypeOperatorHoldEvent,
  MessageTypeOperatorMessage,
  MessageTypeOperatorMessageReceipt,
  MessageTypeOperatorTurnEvent,
  MessageTypeQuestionAnswerResult,
  MessageTypeQuestionClosed,
  MessageTypeUserMessage,
  MessageTypeUserMessageQueued,
  MessageTypeUserMessageReceipt,
  type EscalationAnswerFrame,
  type EscalationAnswerResult,
  type OperatorMessageView,
  type UserMessageView,
} from '@agion/mission-stream';
import type { QuestionReply } from '@agion/mission-stream';
import type {
  ConnectionState,
  WebSocketClientOptions,
  WebSocketEventHandlers,
  MissionSubscriptionOptions,
  InboundStreamingMessage,
  MissionSubscribedMessage,
  OutboundStreamingMessage,
  NodeStatusUpdateMessage,
  EdgeActivationMessage,
  AgentSpeakingMessage,
  VoiceRoomReadyMessage,
  TranscriptInterimMessage,
  TranscriptFinalMessage,
  AgentTurnMessage,
  ToolCallMessage,
  ToolResultMessage,
  HITLRequestMessage,
  HITLResponseMessage,
  OrchestratorDecisionMessage,
  DelegationMessage,
  DelegationResultMessage,
  AgentThinkingMessage,
  GovernanceDecisionMessage,
  MissionTriggerMessage,
  MissionCompletedMessage,
  MissionFailedMessage,
  UserMessageReceiptMessage,
  UserMessageQueuedMessage,
  QuestionAnswerResultMessage,
  QuestionClosedMessage,
  ConnectionRestoredMessage,
  OperatorHoldMessage,
  OperatorTurnMessage,
  OperatorEndUserTurnMessage,
  OperatorMessageReceiptMessage,
  EscalationAnswerResultMessage,
  ErrorMessage,
  HITLDecision,
  HITLAttachment,
} from '@/types/streaming';

const DEFAULT_OPTIONS: Required<WebSocketClientOptions> = {
  autoReconnect: true,
  maxReconnectAttempts: 50,
  reconnectDelay: 1000,
  maxReconnectDelay: 30000,
  heartbeatInterval: 30000,
  connectionTimeout: 10000,
};

// The relay authorizes each mission.subscribe against the run (agion/Agion#2346).
// It answers a refusal with an error frame naming the run, and an accepted
// subscribe with mission.subscribed.
const SUBSCRIPTION_FORBIDDEN = 'subscription_forbidden'
const SUBSCRIPTION_UNAVAILABLE = 'subscription_unavailable'
const CONTROL_FORBIDDEN = 'control_forbidden'
// A subscribe refused as unavailable is sent again after 1 s, doubling to 30 s,
// jittered, at most 6 times; then the refusal reaches the handlers.
const SUBSCRIBE_RETRY_BASE_MS = 1000
const SUBSCRIBE_RETRY_MAX_MS = 30000
const SUBSCRIBE_RETRY_MAX_ATTEMPTS = 6

interface MissionSubscriptionRecord {
  missionId: string
  executionId: string
  handlers: Set<MissionSubscriptionOptions>
  /** The relay refused the last subscribe as unavailable and has not acknowledged one since. */
  refused: boolean
  retryAttempts: number
  retryTimer: ReturnType<typeof setTimeout> | null
  /** A subscribe is on its way and the relay has not answered it. */
  awaitingAnswer: boolean
  /** mission.ready went out behind the unanswered subscribe. */
  readyBehindSubscribe: boolean
  /** mission.ready goes out again when the relay acknowledges the subscribe. */
  readyOwed: boolean
  /** The relay refuses the ready that followed a refused subscribe; that refusal is not shown. */
  readyRefusalExpected: boolean
}

export class WebSocketStreamingClient {
  private url: string;
  private options: Required<WebSocketClientOptions>;
  private ws: WebSocket | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private connectionTimeoutHandle: ReturnType<typeof setTimeout> | null = null;
  private eventHandlers: WebSocketEventHandlers = {};
  private stateChangeCallbacks: ((state: ConnectionState) => void)[] = [];
  private missionSubscriptions = new Map<string, MissionSubscriptionRecord>();
  private escalationAnswerResultListeners = new Set<(result: EscalationAnswerResult) => void>();
  private connectResolver: ((value: void) => void) | null = null;
  private connectRejecter: ((reason: Error) => void) | null = null;

  constructor(url: string, options: WebSocketClientOptions = {}) {
    this.url = url;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Connect to the WebSocket server
   */
  async connect(): Promise<void> {
    if (this.connectionState === 'connected') {
      return;
    }

    if (this.connectionState === 'connecting') {
      return new Promise((resolve, reject) => {
        const originalResolver = this.connectResolver;
        const originalRejecter = this.connectRejecter;
        this.connectResolver = (value) => {
          originalResolver?.(value);
          resolve(value);
        };
        this.connectRejecter = (reason) => {
          originalRejecter?.(reason);
          reject(reason);
        };
      });
    }

    return new Promise((resolve, reject) => {
      this.connectResolver = resolve;
      this.connectRejecter = reject;
      this.setConnectionState('connecting');

      try {
        this.ws = new WebSocket(this.url);
        this.setupWebSocketHandlers();
        this.startConnectionTimeout();
      } catch (error) {
        this.handleConnectionError(error as Error);
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server
   */
  async disconnect(): Promise<void> {
    this.clearReconnectTimeout();
    this.clearHeartbeat();
    this.clearConnectionTimeout();
    this.missionSubscriptions.forEach((record) => this.clearSubscribeRetry(record));
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on intentional close
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setConnectionState('disconnected');
    this.eventHandlers.onDisconnect?.('Client disconnect');
  }

  /**
   * Subscribe to mission updates.
   * Multiple handlers can subscribe to the same mission (fan-out).
   * The wire `mission.subscribe` message is sent on the first handler, and
   * again for a handler that takes the user's waiting messages when no
   * handler before it does: the relay asks the host for that list on each
   * subscribe (phase 6, agion/Agion#2327), and the sidebar's subscription to
   * every active run takes none.
   */
  subscribeMission(missionId: string, executionId: string, options: MissionSubscriptionOptions = {}): void {
    const key = this.missionSubscriptionKey(missionId, executionId)
    let record = this.missionSubscriptions.get(key);
    const isFirst = !record || record.handlers.size === 0;
    const needsWaitingList = !!options.onUserMessageQueued &&
      !Array.from(record?.handlers ?? []).some((handler) => handler.onUserMessageQueued);

    if (!record) {
      record = {
        missionId,
        executionId,
        handlers: new Set(),
        refused: false,
        retryAttempts: 0,
        retryTimer: null,
        awaitingAnswer: false,
        readyBehindSubscribe: false,
        readyOwed: false,
        readyRefusalExpected: false,
      };
      this.missionSubscriptions.set(key, record);
    }
    record.handlers.add(options);

    if (isFirst || needsWaitingList) {
      this.sendSubscribe(record);
    }
  }

  /**
   * Subscribes again to a run this client already follows, so the relay
   * replays what it holds for the run, its open questions among them (P-095
   * Amendment S04-7). A run without a handler here is left alone.
   */
  resubscribeMission(missionId: string, executionId: string): void {
    const record = this.missionSubscriptions.get(this.missionSubscriptionKey(missionId, executionId))
    if (record && record.handlers.size > 0) this.sendSubscribe(record)
  }

  /**
   * Unsubscribe from mission updates.
   * If `options` is provided, removes that specific handler (fan-out aware).
   * If `options` is omitted, removes ALL handlers for backward compatibility.
   * The wire `mission.unsubscribe` message is only sent when the last handler is removed.
   */
  unsubscribeMission(
    missionId: string,
    executionIdOrOptions?: string | MissionSubscriptionOptions,
    maybeOptions?: MissionSubscriptionOptions
  ): void {
    const executionId = typeof executionIdOrOptions === 'string' ? executionIdOrOptions : undefined
    const options = typeof executionIdOrOptions === 'string' ? maybeOptions : executionIdOrOptions
    const records = executionId
      ? [this.missionSubscriptions.get(this.missionSubscriptionKey(missionId, executionId))].filter(
          (record): record is MissionSubscriptionRecord => Boolean(record)
        )
      : Array.from(this.missionSubscriptions.values()).filter((record) => record.missionId === missionId)

    for (const record of records) {
      if (options) {
        record.handlers.delete(options);
      } else {
        // Backward compat: remove all handlers for matching records
        record.handlers.clear();
      }

      // Only send wire message when the Set becomes empty
      if (record.handlers.size === 0) {
        this.clearSubscribeRetry(record);
        this.missionSubscriptions.delete(this.missionSubscriptionKey(record.missionId, record.executionId));
        this.sendMessage({
          type: 'mission.unsubscribe',
          payload: { mission_id: record.missionId, execution_id: record.executionId },
          timestamp: new Date().toISOString(),
        });
      }
    }

    if (records.length === 0 && !options) {
      this.sendMessage({
        type: 'mission.unsubscribe',
        payload: { mission_id: missionId, ...(executionId ? { execution_id: executionId } : {}) },
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Send HITL response to the server
   * This routes the user's response back to the host executing the mission.
   * Returns whether the frame left the socket (phase 4, agion/Agion#2327).
   */
  sendHITLResponse(params: {
    missionId: string;
    executionId?: string;
    nodeId: string;
    requestId: string;
    response: string;
    responseTimeMs: number;
    attachments?: HITLAttachment[];
    /** An approval card's explicit decision, only when it is decided inline (agion/Agion#2618). */
    decision?: HITLDecision;
    /** The answer attempt (AnswerAttempts, @agion/mission-stream; agion/Agion#2767). */
    answerAttemptId: string;
    /** A structured question's answer by option id (agion/Agion#3559). */
    answer?: QuestionReply;
  }): boolean {
    const sent = this.sendMessage({
      type: 'mission.hitl.response',
      payload: {
        mission_id: params.missionId,
        ...(params.executionId ? { execution_id: params.executionId } : {}),
        node_id: params.nodeId,
        request_id: params.requestId,
        response: params.response,
        response_time_ms: params.responseTimeMs,
        ...(params.attachments?.length ? { attachments: params.attachments } : {}),
        ...(params.decision ? { decision: params.decision } : {}),
        answer_attempt_id: params.answerAttemptId,
        ...(params.answer ? { answer: params.answer } : {}),
      },
      timestamp: new Date().toISOString(),
    });

    if (sent) logger.info('Sent HITL response', params.missionId, params.requestId);
    return sent;
  }

  /**
   * Send a message the user wrote while the agent works (phase 6,
   * agion/Agion#2327). Its receipts come back as mission.user.message.receipt.
   * Returns whether the frame left the socket.
   */
  sendUserMessage(message: UserMessageView): boolean {
    return this.sendMessage({ type: MessageTypeUserMessage, payload: message, timestamp: new Date().toISOString() });
  }

  /**
   * Send a reply of the person who holds the run's conversation
   * (agion/Agion#3435). Its receipt comes back as
   * mission.operator.message.receipt. Returns whether the frame left the
   * socket.
   */
  sendOperatorMessage(message: OperatorMessageView): boolean {
    return this.sendMessage({ type: MessageTypeOperatorMessage, payload: message, timestamp: new Date().toISOString() });
  }

  /**
   * Send an escalation answer to the run that asked, through the relay
   * (agion/Agion#3427, owner ruling 2026-10-25). The frame is serialised once
   * and neither kept nor logged: its answer stays with the run. The relay's
   * result comes back to onEscalationAnswerResult. Returns whether the frame
   * left the socket.
   */
  sendEscalationAnswer(frame: EscalationAnswerFrame): boolean {
    return this.sendMessage({ type: MessageTypeEscalationAnswerFrame, payload: frame, timestamp: new Date().toISOString() });
  }

  /**
   * Listen to the relay's content-free results of escalation answers. A
   * result names no run, so it reaches these listeners, not the run's
   * subscriptions. Returns the function that stops listening.
   */
  onEscalationAnswerResult(listener: (result: EscalationAnswerResult) => void): () => void {
    this.escalationAnswerResultListeners.add(listener);
    return () => {
      this.escalationAnswerResultListeners.delete(listener);
    };
  }

  /**
   * Send voice user joined notification to the server
   * This triggers the mission.start dispatch to the host after user connects to LiveKit
   */
  sendVoiceUserJoined(params: { missionId: string; executionId: string }): void {
    this.sendMessage({
      type: 'mission.voice.user_joined',
      payload: {
        mission_id: params.missionId,
        execution_id: params.executionId,
      },
      timestamp: new Date().toISOString(),
    });

    logger.info('Sent voice user joined', params.missionId, params.executionId);
  }

  /**
   * Send mission ready notification to the server
   * This signals that the client has subscribed and is ready to receive mission events.
   * For subscriber-gated missions, this triggers the mission.start dispatch.
   * While the relay has refused the run's subscribe as unavailable, the ready
   * waits for the subscribe's acknowledgement: the relay accepts a ready only
   * on an authorized subscription.
   */
  sendMissionReady(params: { missionId: string; executionId: string }): void {
    const record = this.missionSubscriptions.get(this.missionSubscriptionKey(params.missionId, params.executionId));
    if (record?.refused) {
      record.readyOwed = true;
      logger.info('Mission ready waits for the subscription', params.missionId, params.executionId);
      return;
    }
    const sent = this.sendMissionReadyFrame(params);
    if (sent && record?.awaitingAnswer) record.readyBehindSubscribe = true;
  }

  private sendMissionReadyFrame(params: { missionId: string; executionId: string }): boolean {
    const sent = this.sendMessage({
      type: 'mission.ready',
      payload: {
        mission_id: params.missionId,
        execution_id: params.executionId,
      },
      timestamp: new Date().toISOString(),
    });

    if (sent) logger.info('Sent mission ready', params.missionId, params.executionId);
    return sent;
  }

  /**
   * Send mission stop request to the server (F5 — chat-ui catch-up).
   *
   * The streaming-service relay translates this into a mission.cancel
   * command sent to the Edge host that is executing the mission, which
   * tears down the execution context on the mission-runner. The wire
   * contract is locked in by
   * streaming-service/internal/relay/client_test.go
   * `TestStatusClient_HandleMessage_MissionStop_ChatUI`.
   */
  sendMissionStop(params: { missionId: string; executionId: string }): void {
    this.sendMessage({
      type: 'mission.stop',
      payload: {
        mission_id: params.missionId,
        execution_id: params.executionId,
      },
      timestamp: new Date().toISOString(),
    });

    logger.info('Sent mission stop', params.missionId, params.executionId);
  }

  /**
   * Send mission pause request to the server.
   *
   * The streaming-service relays this as a mission.pause command to the
   * Edge host executing the mission, which suspends the execution at the
   * next checkpoint. Same wire contract as admin-ui's designer.
   */
  sendMissionPause(params: { missionId: string; executionId: string }): void {
    this.sendMessage({
      type: 'mission.pause',
      payload: {
        mission_id: params.missionId,
        execution_id: params.executionId,
      },
      timestamp: new Date().toISOString(),
    });

    logger.info('Sent mission pause', params.missionId, params.executionId);
  }

  /**
   * Send mission resume request to the server.
   * Resumes a previously paused execution on the Edge host.
   */
  sendMissionResume(params: { missionId: string; executionId: string }): void {
    this.sendMessage({
      type: 'mission.resume',
      payload: {
        mission_id: params.missionId,
        execution_id: params.executionId,
      },
      timestamp: new Date().toISOString(),
    });

    logger.info('Sent mission resume', params.missionId, params.executionId);
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers: WebSocketEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  /**
   * Subscribe to connection state changes
   */
  onStateChange(callback: (state: ConnectionState) => void): () => void {
    this.stateChangeCallbacks.push(callback);
    return () => {
      this.stateChangeCallbacks = this.stateChangeCallbacks.filter((cb) => cb !== callback);
    };
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * Check if reconnecting
   */
  isReconnecting(): boolean {
    return this.connectionState === 'reconnecting';
  }

  // Private methods

  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return;
    this.connectionState = state;
    this.stateChangeCallbacks.forEach((cb) => cb(state));
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      this.clearConnectionTimeout();
      this.setConnectionState('connected');
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.eventHandlers.onConnect?.();
      this.connectResolver?.();
      this.connectResolver = null;
      this.connectRejecter = null;

      // Resubscribe to missions after reconnect. The new connection holds no
      // subscription, so each record's retries start over.
      this.missionSubscriptions.forEach((record) => {
        record.retryAttempts = 0;
        record.readyRefusalExpected = false;
        this.sendSubscribe(record);
      });
    };

    this.ws.onclose = (event) => {
      this.clearHeartbeat();
      this.clearConnectionTimeout();
      // The reconnect subscribes every record again.
      this.missionSubscriptions.forEach((record) => this.clearSubscribeRetry(record));

      // Normal closure or intentional disconnect
      if (event.code === 1000) {
        this.setConnectionState('disconnected');
        this.eventHandlers.onDisconnect?.(event.reason);
        return;
      }

      // Unexpected closure - attempt reconnect
      if (this.options.autoReconnect && this.reconnectAttempts < this.options.maxReconnectAttempts) {
        this.setConnectionState('reconnecting');
        this.scheduleReconnect();
      } else {
        this.setConnectionState('disconnected');
        this.eventHandlers.onDisconnect?.(event.reason);
      }
    };

    this.ws.onerror = () => {
      const error = new Error('WebSocket error');
      logger.error('WebSocket streaming error');
      this.handleConnectionError(error);
    };

    this.ws.onmessage = (event) => {
      // Backend may batch multiple messages in one frame separated by newlines
      const lines = (event.data as string).split('\n').filter((line: string) => line.trim());

      for (const line of lines) {
        try {
          const rawMessage = JSON.parse(line) as {
            type: string;
            payload?: Record<string, unknown>;
            timestamp?: string;
          };
          // Flatten the message: merge payload fields with type and timestamp
          const message = {
            type: rawMessage.type,
            timestamp: rawMessage.timestamp,
            ...rawMessage.payload,
          } as InboundStreamingMessage;
          this.handleMessage(message);
        } catch {
          logger.error('Failed to parse WebSocket message', line);
        }
      }
    };
  }

  private handleMessage(message: InboundStreamingMessage): void {
    // Handle pong messages (heartbeat response)
    if (message.type === 'pong') {
      return;
    }

    // Invoke global message handler
    this.eventHandlers.onMessage?.(message);

    if (message.type === MessageTypeEscalationAnswerResult) {
      const { escalation_id, outcome, reason } = message as EscalationAnswerResultMessage;
      const result: EscalationAnswerResult = { escalation_id, outcome, ...(reason ? { reason } : {}) };
      for (const listener of [...this.escalationAnswerResultListeners]) listener(result);
      return;
    }

    // Route mission-specific messages to all subscription handlers (fan-out).
    // A mission trigger names the sub-mission in mission_id and execution_id,
    // so it goes to this socket's subscriptions of the parent mission. The
    // relay sends it only to sockets that read the parent run
    // (StatusHub.BroadcastMissionTrigger, agion/Agion#2575).
    let records: MissionSubscriptionRecord[] = [];
    if (message.type === 'mission.trigger') {
      records = this.findMissionSubscriptions((message as MissionTriggerMessage).parent_mission_id);
    } else if (message.type === MessageTypeQuestionAnswerResult) {
      // The result names its run inside the answer it is about (agion/Agion#2767).
      const { answer } = message as QuestionAnswerResultMessage;
      if (answer?.mission_id && answer.execution_id) {
        records = this.findMissionSubscriptions(answer.mission_id, answer.execution_id);
      }
    } else if (message.type === MessageTypeOperatorMessageReceipt) {
      // An operator reply's receipt names only its run (agion/Agion#3435).
      const { execution_id: executionId } = message as OperatorMessageReceiptMessage;
      records = Array.from(this.missionSubscriptions.values()).filter((record) => record.executionId === executionId);
    } else if ('mission_id' in message) {
      const missionId = (message as { mission_id: string }).mission_id;
      const executionId = (message as { execution_id?: string }).execution_id;
      records = this.findMissionSubscriptions(missionId, executionId);
      const [record] = records;
      if (executionId && record && !this.answerRunAuthorization(record, message)) return;
    }
    for (const record of records) {
      for (const subscription of record.handlers) {
        this.routeToSubscriptionHandler(message, subscription);
      }
    }
  }

  /**
   * Applies the relay's answer to a subscribe or a control frame for one run
   * (agion/Agion#2346). Returns false when the frame is handled here and does
   * not reach the run's handlers.
   */
  private answerRunAuthorization(record: MissionSubscriptionRecord, message: InboundStreamingMessage): boolean {
    if (message.type === 'mission.subscribed') {
      this.clearSubscribeRetry(record);
      record.refused = false;
      record.retryAttempts = 0;
      record.awaitingAnswer = false;
      record.readyBehindSubscribe = false;
      record.readyRefusalExpected = false;
      if (record.readyOwed) {
        record.readyOwed = false;
        this.sendMissionReadyFrame({ missionId: record.missionId, executionId: record.executionId });
      }
      return true;
    }
    if (message.type !== 'error') return true;

    switch ((message as ErrorMessage).code) {
      case SUBSCRIPTION_UNAVAILABLE:
        return !this.retrySubscribe(record);
      case SUBSCRIPTION_FORBIDDEN:
        // Terminal: the handlers see the refusal, and no reconnect sends the
        // subscribe again.
        this.clearSubscribeRetry(record);
        this.missionSubscriptions.delete(this.missionSubscriptionKey(record.missionId, record.executionId));
        return true;
      case CONTROL_FORBIDDEN:
        if (record.readyRefusalExpected) {
          record.readyRefusalExpected = false;
          return false;
        }
        return true;
      default:
        return true;
    }
  }

  /**
   * Schedules the subscribe again after a refusal as unavailable. Returns
   * false when the attempts are spent; the next reconnect subscribes again.
   */
  private retrySubscribe(record: MissionSubscriptionRecord): boolean {
    record.refused = true;
    record.awaitingAnswer = false;
    if (record.readyBehindSubscribe) {
      // The relay refuses that ready too; it goes out again after the acknowledgement.
      record.readyBehindSubscribe = false;
      record.readyOwed = true;
      record.readyRefusalExpected = true;
    }
    this.clearSubscribeRetry(record);
    if (record.retryAttempts >= SUBSCRIBE_RETRY_MAX_ATTEMPTS) return false;

    const ceiling = Math.min(SUBSCRIBE_RETRY_BASE_MS * 2 ** record.retryAttempts, SUBSCRIBE_RETRY_MAX_MS);
    const delay = ceiling / 2 + Math.random() * (ceiling / 2);
    record.retryAttempts++;
    logger.info('Subscription unavailable, retrying', record.missionId, record.executionId, `delay=${Math.round(delay)}ms`);
    const key = this.missionSubscriptionKey(record.missionId, record.executionId);
    record.retryTimer = setTimeout(() => {
      record.retryTimer = null;
      if (this.missionSubscriptions.get(key) === record) this.sendSubscribe(record);
    }, delay);
    return true;
  }

  private sendSubscribe(record: MissionSubscriptionRecord): void {
    this.clearSubscribeRetry(record);
    record.readyBehindSubscribe = false;
    record.awaitingAnswer = this.sendMessage({
      type: 'mission.subscribe',
      payload: { mission_id: record.missionId, execution_id: record.executionId },
      timestamp: new Date().toISOString(),
    });
  }

  private clearSubscribeRetry(record: MissionSubscriptionRecord): void {
    if (record.retryTimer) {
      clearTimeout(record.retryTimer);
      record.retryTimer = null;
    }
  }

  private missionSubscriptionKey(missionId: string, executionId: string): string {
    return `${missionId}\u0000${executionId}`
  }

  private findMissionSubscriptions(missionId: string, executionId?: string): MissionSubscriptionRecord[] {
    if (executionId) {
      const subscription = this.missionSubscriptions.get(this.missionSubscriptionKey(missionId, executionId))
      return subscription ? [subscription] : []
    }
    return Array.from(this.missionSubscriptions.values()).filter((record) => record.missionId === missionId)
  }

  private routeToSubscriptionHandler(
    message: InboundStreamingMessage,
    subscription: MissionSubscriptionOptions
  ): void {
    switch (message.type) {
      case 'mission.node.status':
        subscription.onNodeStatusUpdate?.(message as NodeStatusUpdateMessage);
        break;
      case 'mission.edge.activate':
        subscription.onEdgeActivation?.(message as EdgeActivationMessage);
        break;
      case 'mission.agent.speaking':
        subscription.onAgentSpeaking?.(message as AgentSpeakingMessage);
        break;
      case 'mission.voice.room_ready':
        subscription.onVoiceRoomReady?.(message as VoiceRoomReadyMessage);
        break;
      case 'mission.transcript.interim':
        subscription.onTranscriptInterim?.(message as TranscriptInterimMessage);
        break;
      case 'mission.transcript.final':
        subscription.onTranscriptFinal?.(message as TranscriptFinalMessage);
        break;
      case 'mission.agent.turn':
        subscription.onAgentTurn?.(message as AgentTurnMessage);
        break;
      case 'mission.tool.call':
        subscription.onToolCall?.(message as ToolCallMessage);
        break;
      case 'mission.tool.result':
        subscription.onToolResult?.(message as ToolResultMessage);
        break;
      case 'mission.hitl.request':
        subscription.onHITLRequest?.(message as HITLRequestMessage);
        break;
      case 'mission.hitl.response':
        subscription.onHITLResponse?.(message as HITLResponseMessage);
        break;
      case 'mission.orchestrator.decision':
        subscription.onOrchestratorDecision?.(message as OrchestratorDecisionMessage);
        break;
      case 'mission.delegation':
        subscription.onDelegation?.(message as DelegationMessage);
        break;
      case 'mission.delegation.result':
        subscription.onDelegationResult?.(message as DelegationResultMessage);
        break;
      case 'mission.agent.thinking':
        subscription.onAgentThinking?.(message as AgentThinkingMessage);
        break;
      case 'mission.governance.decision':
        subscription.onGovernanceDecision?.(message as GovernanceDecisionMessage);
        break;
      case 'mission.trigger':
        subscription.onMissionTrigger?.(message as MissionTriggerMessage);
        break;
      case 'mission.completed':
        subscription.onCompleted?.(message as MissionCompletedMessage);
        break;
      case 'mission.stopped':
        // User-initiated stop — treat as completed (not an error)
        subscription.onCompleted?.({
          type: 'mission.completed',
          mission_id: (message as any).mission_id ?? '',
          outcome: 'cancelled',
          duration_ms: (message as any).duration_ms ?? 0,
        } as MissionCompletedMessage);
        break;
      case 'mission.failed':
        subscription.onFailed?.(message as MissionFailedMessage);
        break;
      case MessageTypeUserMessageReceipt:
        subscription.onUserMessageReceipt?.(message as UserMessageReceiptMessage);
        break;
      // The host's answer to this subscribe: the user's messages that still wait.
      case MessageTypeUserMessageQueued:
        subscription.onUserMessageQueued?.(message as UserMessageQueuedMessage);
        break;
      case MessageTypeQuestionAnswerResult:
        subscription.onQuestionAnswerResult?.(message as QuestionAnswerResultMessage);
        break;
      case MessageTypeQuestionClosed:
        subscription.onQuestionClosed?.(message as QuestionClosedMessage);
        break;
      case MessageTypeConnectionRestored:
        subscription.onConnectionRestored?.(message as ConnectionRestoredMessage);
        break;
      case MessageTypeOperatorHoldEvent:
        subscription.onOperatorHold?.(message as OperatorHoldMessage);
        break;
      case MessageTypeOperatorTurnEvent:
        subscription.onOperatorTurn?.(message as OperatorTurnMessage);
        break;
      case MessageTypeOperatorEndUserTurnEvent:
        subscription.onOperatorEndUserTurn?.(message as OperatorEndUserTurnMessage);
        break;
      case MessageTypeOperatorMessageReceipt:
        subscription.onOperatorMessageReceipt?.(message as OperatorMessageReceiptMessage);
        break;
      case 'mission.subscribed':
        subscription.onSubscribed?.(message as MissionSubscribedMessage);
        break;
      case 'error':
        subscription.onError?.(message as ErrorMessage);
        break;
    }
  }

  /** Sends a frame; returns false when the socket is not open and nothing left. */
  private sendMessage(message: OutboundStreamingMessage): boolean {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
      return true;
    }
    logger.warn('Cannot send message, WebSocket not connected', message.type);
    return false;
  }

  private handleConnectionError(error: Error): void {
    this.clearConnectionTimeout();
    this.setConnectionState('error');
    this.eventHandlers.onError?.(error);
    this.connectRejecter?.(error);
    this.connectResolver = null;
    this.connectRejecter = null;
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    const delay = Math.min(
      this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.options.maxReconnectDelay
    );

    logger.debug('Scheduling reconnect', `attempt=${this.reconnectAttempts}`, `delay=${delay}ms`);

    this.eventHandlers.onReconnecting?.(this.reconnectAttempts);

    this.reconnectTimeout = setTimeout(() => {
      try {
        this.ws = new WebSocket(this.url);
        this.setupWebSocketHandlers();
      } catch {
        if (this.reconnectAttempts >= this.options.maxReconnectAttempts) {
          this.setConnectionState('error');
          this.eventHandlers.onError?.(
            new Error(`Failed to reconnect after ${this.options.maxReconnectAttempts} attempts`)
          );
        } else {
          this.scheduleReconnect();
        }
      }
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({
        type: 'ping',
        timestamp: new Date().toISOString(),
      });
    }, this.options.heartbeatInterval);
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private startConnectionTimeout(): void {
    this.connectionTimeoutHandle = setTimeout(() => {
      if (this.connectionState === 'connecting') {
        this.handleConnectionError(new Error('Connection timeout'));
        this.ws?.close();
      }
    }, this.options.connectionTimeout);
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeoutHandle) {
      clearTimeout(this.connectionTimeoutHandle);
      this.connectionTimeoutHandle = null;
    }
  }
}
